import { SIDE } from 'constants/enums';
import { sortBy } from 'helpers/sortBy';
import { isNan } from 'helpers/utils';
import { OpenOrder } from 'types/IOpenOrders';
import { OpenPosition } from 'types/IOpenPosition';
import { ContractType, NotionalType, Product } from 'types/IProducts';

const notional = (
  size: number,
  price: number,
  { contract_value: contractValue = '1', notional_type: notionalType }: Product
) => {
  try {
    if (notionalType === NotionalType.Vanilla) {
      return Math.abs(size) * Number(contractValue) * price;
    }
    if (notionalType === NotionalType.Inverse) {
      return (Math.abs(size) / price) * Number(contractValue);
    }
    throw new Error('Invalid notional type');
  } catch (error) {
    console.error('Error in notional calculation:', error);
    return null;
  }
};

/**
 * This function sanitizes the buy and sell pairs to be used in the margin formulae. sort and add the current order to the sorted open buy order
 * @param openBuyOrder - The open buy orders
 * @param openSellOrder - The open sell orders
 * @param size - The size of the order
 * @param price - The price of the order
 * @param side - The side of the order
 * @returns The sanitized buy and sell pairs
 */
const sanitizeBuySellPairs = ({
  openBuyOrder,
  openSellOrder,
  size,
  price,
  side,
}: {
  openBuyOrder: OpenOrder[];
  openSellOrder: OpenOrder[];
  size: number;
  price: number;
  side: SIDE;
}): null | {
  sortedOpenBuyOrder: {
    unfilled_size: number | undefined;
    limit_price: string | undefined;
  }[];
  sortedOpenSellOrder: {
    unfilled_size: number | undefined;
    limit_price: string | undefined;
  }[];
} => {
  try {
    // Function to pick necessary fields from an order
    const pickFields = (order: OpenOrder) => ({
      unfilled_size: order.unfilled_size ?? 0,
      limit_price: order.limit_price,
    });

    // Trim the open buy and sell orders to only include necessary fields
    const trimmedOpenBuyOrder = openBuyOrder.map(pickFields);
    const trimmedOpenSellOrder = openSellOrder.map(pickFields);

    // Sort the trimmed open buy orders in descending order of limit price
    const sortedOpenBuyOrder = sortBy(
      trimmedOpenBuyOrder,
      order => Number(order.limit_price),
      'descending'
    );

    // Sort the trimmed open sell orders in ascending order of limit price
    const sortedOpenSellOrder = sortBy(
      trimmedOpenSellOrder,
      order => Number(order.limit_price),
      'ascending'
    );

    // Add the current order to the sorted open orders based on the side (buy/sell)
    switch (side) {
      case SIDE.BUY:
        sortedOpenBuyOrder.push({
          unfilled_size: size,
          limit_price: String(price),
        });
        break;
      case SIDE.SELL:
        sortedOpenSellOrder.push({
          unfilled_size: size,
          limit_price: String(price),
        });
        break;
      default:
        throw new Error(`Unsupported side: ${side}`);
    }

    // Return the sanitized and sorted buy and sell orders
    return {
      sortedOpenBuyOrder,
      sortedOpenSellOrder,
    };
  } catch (error) {
    console.error('Error in sanitizeBuySellPairs:', error);
    return null;
  }
};

const offsetOpenPositionsFromOrders = ({
  position,
  buyOrders,
  sellOrders,
}: {
  position: OpenPosition | null;
  buyOrders: { unfilled_size: number | undefined; limit_price: string | undefined }[];
  sellOrders: { unfilled_size: number | undefined; limit_price: string | undefined }[];
}) => {
  try {
    // If there is no position size, return the original buy and sell orders
    if (!position?.size) {
      return {
        adjustedBuyOrders: buyOrders,
        adjustedSellOrders: sellOrders,
      };
    }

    // Determine the cancel size as the absolute value of the position size
    let cancelSize = Math.abs(position.size);

    // Select the pairs to offset based on the position size (positive for sell orders, negative for buy orders)
    const pairs = position.size > 0 ? sellOrders : buyOrders;

    // Reduce the pairs to calculate the offset pairs
    const offsetPairs = pairs.reduce(
      (acc: { unfilled_size?: number; limit_price?: string }[], pair) => {
        const availableSize = pair.unfilled_size ?? 0; // Get the available size of the current pair
        const offsetSize = Math.min(cancelSize, availableSize); // Determine the offset size
        const remainingSize = availableSize - offsetSize; // Calculate the remaining size after offset
        cancelSize -= offsetSize; // Decrement the cancelSize by the offsetSize

        // If there is remaining size, add it to the accumulator
        if (remainingSize > 0) {
          acc.push({
            unfilled_size: remainingSize,
            limit_price: pair.limit_price,
          });
        }

        return acc; // Return the accumulator for the next iteration
      },
      []
    );

    // Return the adjusted buy and sell orders based on the position size
    return {
      adjustedBuyOrders: position.size < 0 ? offsetPairs : buyOrders,
      adjustedSellOrders: position.size > 0 ? offsetPairs : sellOrders,
    };
  } catch (error) {
    console.error('Error in offsetOpenPositionsFromOrders:', error);
    return null;
  }
};

/**
 * Calculate the margins for each pair of buy and sell orders.
 * @param {Array} buyPairs - Array of buy order pairs.
 * @param {Array} sellPairs - Array of sell order pairs.
 * @returns {Array} - Array of margin objects.
 *
 * Example Input:
 * buyPairs = [
 *   { unfilled_size: 10, limit_price: '100' },
 *   { unfilled_size: 5, limit_price: '105' }
 * ]
 * sellPairs = [
 *   { unfilled_size: 8, limit_price: '95' },
 *   { unfilled_size: 7, limit_price: '90' }
 * ]
 *
 * Example Output:
 * [
 *   { size: 8, buyLimitPrice: '100', sellLimitPrice: '95' },
 *   { size: 2, buyLimitPrice: '100', sellLimitPrice: '90' },
 *   { size: 5, buyLimitPrice: '105', sellLimitPrice: '90' }
 * ]
 */
const calculateOffsetMargins = ({
  buyPairs,
  sellPairs,
}: {
  buyPairs: { unfilled_size: number | undefined; limit_price: string | undefined }[];
  sellPairs: { unfilled_size: number | undefined; limit_price: string | undefined }[];
}): { size: number; buyLimitPrice: string | null; sellLimitPrice: string | null }[] => {
  let buyIndex = 0;
  let sellIndex = 0;

  const margins: {
    size: number;
    buyLimitPrice: string | null;
    sellLimitPrice: string | null;
  }[] = [];

  // Helper function to add a margin entry
  const addMargin = (
    size: number,
    buyLimitPrice: string | null,
    sellLimitPrice: string | null
  ) => {
    if (size > 0) {
      margins.push({ size, buyLimitPrice, sellLimitPrice });
    }
  };

  // Match buy and sell pairs
  try {
    while (buyIndex < buyPairs.length && sellIndex < sellPairs.length) {
      const buyPair = buyPairs[buyIndex];
      const sellPair = sellPairs[sellIndex];
      if (!buyPair.unfilled_size || !sellPair.unfilled_size) {
        throw new Error('Unexpected undefined unfilled size');
      }
      if (!buyPair.limit_price || !sellPair.limit_price) {
        throw new Error('Unexpected undefined limit price');
      }

      const sizeToMatch = Math.min(buyPair.unfilled_size, sellPair.unfilled_size);

      addMargin(sizeToMatch, buyPair.limit_price, sellPair.limit_price);

      // Update unfilled sizes
      buyPair.unfilled_size -= sizeToMatch;
      sellPair.unfilled_size -= sizeToMatch;

      // Move to the next pair if the current one is fully matched
      if (buyPair.unfilled_size === 0) buyIndex += 1;
      if (sellPair.unfilled_size === 0) sellIndex += 1;
    }
  } catch (error) {
    console.error('Error while matching buy and sell pairs:', error);
  }

  // Add remaining buy orders with no matching sell orders
  try {
    while (buyIndex < buyPairs.length) {
      const buyPair = buyPairs[buyIndex];
      if (buyPair.unfilled_size === undefined) {
        throw new Error('Unexpected undefined unfilled size');
      }
      if (buyPair.limit_price === undefined) {
        throw new Error('Unexpected undefined limit price');
      }
      addMargin(buyPair.unfilled_size, buyPair.limit_price, null);
      buyIndex += 1;
    }
  } catch (error) {
    console.error('Error while processing remaining buy orders:', error);
  }

  // Add remaining sell orders with no matching buy orders
  try {
    while (sellIndex < sellPairs.length) {
      const sellPair = sellPairs[sellIndex];
      if (sellPair.unfilled_size === undefined) {
        throw new Error('Unexpected undefined unfilled size');
      }
      if (sellPair.limit_price === undefined) {
        throw new Error('Unexpected undefined limit price');
      }
      addMargin(sellPair.unfilled_size, null, sellPair.limit_price);
      sellIndex += 1;
    }
  } catch (error) {
    console.error('Error while processing remaining sell orders:', error);
  }

  return margins;
};

const limitPriceBasedOnContractType = ({
  selectedProduct,
  buyLimitPrice,
  sellLimitPrice,
}: {
  selectedProduct: Product | null;
  buyLimitPrice: string | null;
  sellLimitPrice: string | null;
}): { limitPrice: string | null; side: SIDE } => {
  const contractType = selectedProduct?.contract_type;

  switch (contractType) {
    case ContractType.CallOptions:
    case ContractType.PutOptions:
    case ContractType.TurboCallOptions:
    case ContractType.TurboPutOptions:
    case ContractType.MoveOptions:
    case ContractType.OptionsCombos:
      if (sellLimitPrice) {
        return { limitPrice: sellLimitPrice, side: SIDE.SELL };
      }
      return { limitPrice: buyLimitPrice, side: SIDE.BUY };
    case ContractType.Spreads:
      return { limitPrice: sellLimitPrice ?? buyLimitPrice, side: SIDE.SELL };
    default:
      if (selectedProduct?.notional_type === NotionalType.Inverse) {
        return { limitPrice: buyLimitPrice ?? sellLimitPrice, side: SIDE.BUY };
      }
      return { limitPrice: sellLimitPrice ?? buyLimitPrice, side: SIDE.SELL };
  }
};

const isSpotContract = contractType => contractType === 'spot';

const offsetPositionSize = ({
  maxNumberofContracts,
  totalPositionSize,
  side,
}: {
  maxNumberofContracts: number;
  totalPositionSize: number;
  side: SIDE;
}) => {
  let offsettedPositionSize = 0;
  switch (side) {
    case SIDE.BUY:
      offsettedPositionSize = Math.abs(Math.min(totalPositionSize, 0.0));
      break;
    case SIDE.SELL:
      offsettedPositionSize = Math.max(totalPositionSize, 0.0);
      break;
    default:
      throw new Error(`Unsupported side: ${side}`);
  }
  return Math.floor(maxNumberofContracts + offsettedPositionSize);
};

const sanitizeMaxQuantity = ({
  maxQuantity,
  totalPositionSize,
  side,
  product,
}: {
  maxQuantity: number;
  totalPositionSize: number;
  side: SIDE;
  product: Product;
}) => {
  try {
    if (!product.contract_value || !product.position_size_limit) {
      throw new Error('Product contract value or position size limit is undefined');
    }
    const contractValue = Number(product.contract_value);
    const positionSizeLimit = Number(product.position_size_limit);

    if (isNan(contractValue)) {
      throw new Error('Product contract value is undefined');
    }
    if (isNan(positionSizeLimit)) {
      throw new Error('Product contract value or position size limit is undefined');
    }

    // Calculate the initial optimized max quantity by dividing maxQuantity by contractValue
    let optimizedMaxQuantity = Math.floor(maxQuantity / contractValue);

    // Adjust the optimized max quantity based on the action state (buy or sell)
    optimizedMaxQuantity = offsetPositionSize({
      maxNumberofContracts: optimizedMaxQuantity,
      totalPositionSize,
      side,
    });

    // Ensure the optimized max quantity does not exceed the position size limit
    switch (side) {
      case SIDE.BUY:
        optimizedMaxQuantity = Math.min(
          positionSizeLimit - Math.max(totalPositionSize, 0.0),
          optimizedMaxQuantity
        );
        break;
      case SIDE.SELL:
        optimizedMaxQuantity = Math.min(
          positionSizeLimit - Math.abs(Math.min(totalPositionSize, 0.0)),
          optimizedMaxQuantity
        );
        break;
      default:
        throw new Error('Invalid side');
    }

    return optimizedMaxQuantity;
  } catch (error) {
    console.error('Error in sanitizeMaxQuantity:', error);
    return null;
  }
};

export {
  calculateOffsetMargins,
  isSpotContract,
  limitPriceBasedOnContractType,
  notional,
  offsetOpenPositionsFromOrders,
  offsetPositionSize,
  sanitizeBuySellPairs,
  sanitizeMaxQuantity,
};
